import Helpers from "utils/Helpers"
import { fetchWrapper } from "lib/FetchWrapper/FetchWrapper"
import { CarrotErrorCodes } from "utils/CarrotErrors"
import {
  DeductibleStatusModel,
  MerchantValidModel,
  RegisteringUserModel,
  ReimbursementDetailsModel
} from "lib/carrot-api/Models"
import { reportError } from "utils/ErrorReporting"
import { HttpErrors } from "utils/HttpErrors"
import { UserInfoModel } from "lib/carrot-api/UserInfoModel"

const CarrotClient = function (baseUrl) {
  this.baseUrl = baseUrl
}

CarrotClient.prototype = {
  apiV2PostJson: async function (path, payload) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify(payload)
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    try {
      return JSON.parse(await response.text())
    } catch {
      return null
    }
  },

  apiV2RequestWithRedirect: async function (request) {
    let response
    try {
      response = await request()
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        await Helpers.browserRedirect(`${window.location.pathname}?errorCode=${CarrotErrorCodes.NOT_AUTHENTICATED}`)
        return
      }
      throw error
    }
    return response
  },

  apiV2JsonWithRedirect: async function (path, method, payload = null) {
    return this.apiV2RequestWithRedirect(async () => {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const body = payload ? JSON.stringify(payload) : null

      return fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
        method,
        headers,
        body,
        credentials: "include"
      })
    })
  },

  apiV2GetJson: async function (path) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
      method: "GET",
      headers,
      credentials: "include"
    })

    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  signUp: async function (email) {
    return this.apiV2PostJson("sign-up", { email })
  },

  signUpFirstLastDateOfBirth: async function (email, firstName, lastName, dateOfBirth) {
    return this.apiV2PostJson("sign-up/find-account", { email, firstName, lastName, dateOfBirth })
  },

  signUpAddEmailToAccount: async function (email, firstName, lastName, dateOfBirth) {
    return this.apiV2PostJson("sign-up/email", { email, firstName, lastName, dateOfBirth })
  },

  logout: async function () {
    return this.apiV2PostJson("auth/logout", {})
  },

  sendTalkToCarrotMessage: async function (message, topic, sendFromPartner, provider) {
    return this.apiV2PostJson("talk-to-carrot/send-talk-to-carrot-message", {
      message,
      topic,
      sendFromPartner,
      provider
    })
  },

  sendTalkToCarrotSignUpMessage: async function (message, topic, sendFromPartner, provider, email, name) {
    return this.apiV2PostJson("talk-to-carrot-signup/get-help", {
      message,
      topic,
      sendFromPartner,
      provider,
      email,
      name
    })
  },

  lookup: async function (email) {
    return await this.apiV2PostJson("users/lookup", { email })
  },

  login: async function (email, password) {
    return await this.apiV2PostJson("auth/login", { email, password })
  },

  validateResetPasswordGuid: async function (passwordResetGuid) {
    return this.apiV2PostJson("auth/validate-reset-guid", {
      passwordResetGuid
    })
  },

  resetPassword: async function (password, confirmPassword, passwordResetGuid) {
    return this.apiV2PostJson("auth/reset-password", {
      password,
      confirmPassword,
      passwordResetGuid
    })
  },

  submitCardExpense: async function (
    fileGuids,
    whoReceivedCare,
    isInfertility,
    isMedicalNecessity,
    memberNotes,
    expenseId,
    expenseJourneyType
  ) {
    return this.apiV2JsonWithRedirect("statements/submitCardExpense", "POST", {
      fileGuids,
      whoReceivedCare,
      isInfertility,
      isMedicalNecessity,
      memberNotes,
      expenseId,
      expenseJourneyType
    })
  },

  submitExpense: async function (
    fileGuids,
    whoReceivedCare,
    isInfertility,
    isMedicalNecessity,
    memberNotes,
    expenseJourneyType
  ) {
    return this.apiV2JsonWithRedirect("statements/submitExpense", "POST", {
      fileGuids,
      whoReceivedCare,
      isInfertility,
      isMedicalNecessity,
      memberNotes,
      expenseJourneyType
    })
  },

  startPhoneVerification: async function (phoneNumber, countryCode) {
    return this.apiV2JsonWithRedirect("phoneVerification/start", "POST", {
      phoneNumber,
      countryCode
    })
  },

  completePhoneVerification: async function (phoneNumber, code, countryCode) {
    return this.apiV2JsonWithRedirect("phoneVerification/complete", "POST", {
      phoneNumber,
      code,
      countryCode
    })
  },

  completePhoneVerificationAndRequestCard: async function (phoneNumber, code, countryCode) {
    return this.apiV2JsonWithRedirect("card/verification/complete", "POST", {
      phoneNumber,
      code,
      countryCode
    })
  },

  createCardholder: async function (address1, address2, city, state, zip) {
    return this.apiV2JsonWithRedirect("card/createcardholder", "POST", {
      address1,
      address2,
      city,
      state,
      zip
    })
  },

  // Registration Methods

  createAccount: async function (guid, password, emailOptIn, agreeToTerms) {
    return this.apiV2JsonWithRedirect(`register/${guid}`, "POST", {
      guid,
      password,
      emailOptIn,
      agreeToTerms
    })
  },

  validatePassword: async function (password) {
    return this.apiV2JsonWithRedirect(`register/verify-password`, "POST", {
      password
    })
  },

  updatePassword: async function ({ currentPassword, newPassword, confirmNewPassword }) {
    return this.apiV2PostJson("auth/update-password", {
      currentPassword,
      newPassword,
      confirmNewPassword
    })
  },

  getRegisteringUser: async function (guid) {
    try {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const response = await fetchWrapper(`${this.baseUrl}/api/v2/register/${guid}`, {
        method: "GET",
        headers,
        credentials: "include"
      })
      const responseText = await response.text()
      const responseJson = JSON.parse(responseText)
      return Object.assign(new RegisteringUserModel(), responseJson)
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        await Helpers.browserRedirect(`/?errorCode=${CarrotErrorCodes.EMPLOYEE_ALREADY_REGISTERED}`)
        return
      }
      reportError(error)
    }
  },

  // API V2 File Upload Request

  uploadReimbursementFile: async function (file, guid) {
    const response = await this.apiV2RequestWithRedirect(async () => {
      const body = new FormData()
      body.append("file", file)
      body.append("guid", guid)

      return fetchWrapper(this.baseUrl + "/api/v2/statements/uploadFile", {
        method: "POST",
        body,
        credentials: "include"
      })
    })

    const responseJson = await response.json()
    if (!responseJson.hasOwnProperty("fileGuid")) {
      throw new Error(`Response does not have field "fileGuid": ${JSON.stringify(responseJson)}`)
    }
    return responseJson.fileGuid
  },

  activateCard: function () {
    return this.apiV2JsonWithRedirect("card/activate", "POST", {})
  },

  getCardDetails: async function () {
    const response = await this.apiV2JsonWithRedirect("card/details", "GET")
    return await response.json()
  },

  getCurrentUser: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new UserInfoModel(), responseJson)
  },

  checkIfMerchantValid: async function (token) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/merchant/isValid/${token}`, {
      method: "GET",
      headers
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new MerchantValidModel(), responseJson)
  },

  approveMerchant: async function (token) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/merchant/approve/${token}`, {
      method: "POST",
      headers
    })
  },

  getReimbursementDetails: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/reimbursement-details`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new ReimbursementDetailsModel(), responseJson)
  },

  verifyMemberInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-member-insurance", null)
  },

  verifyPartnerInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-partner-insurance", null)
  },

  updateUser: function (userInfoModel: UserInfoModel) {
    return this.apiV2JsonWithRedirect("users/me", "PATCH", userInfoModel)
  },

  forgotPassword: async function (email) {
    return this.apiV2PostJson("users/forgotPassword", { email })
  },

  getSupportCenterUrl: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/zendesk/support-center-url`, {
      method: "GET",
      headers,
      credentials: "include"
    })

    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)

    return responseJson.supportCenterUrl
  },

  getBenefitConfiguration: async function () {
    return await this.apiV2GetJson("users/me/benefit-configuration")
  },

  updateBenefitEnrollments: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/update-benefit-enrollments", "POST")
    return response.json()
  },

  // New endpoint to eventually replace 'updateBenefitEnrollments'
  enrollUserBenefitEnrollment: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/benefit-enrollment", "POST")
    return response
  },

  getBenefitEnrollmentPreview: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/benefit-enrollment/preview`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    return JSON.parse(responseText)
  }
}

export default CarrotClient
